/**
 * Starter projects.
 *
 * PRD §8.2 asks whether v1 should lead electrical or mechanical. Shipping one
 * worked example of each answers it without picking: the engine is genuinely
 * domain-agnostic, and the fastest way to show that is two projects that share
 * no vocabulary and run through identical machinery.
 */

import type { Project } from '../engine/model.ts';

export interface Example {
  id: string;
  title: string;
  blurb: string;
  build: () => Project;
}

export const EXAMPLES: Example[] = [
  {
    id: 'divider',
    title: 'Voltage divider over temperature',
    blurb:
      'A 1% divider with matched resistors from one reel, drifting together over −40…85 °C. Shows why correlation and shared globals matter.',
    build: () => ({
      schemaVersion: 1,
      name: 'Voltage divider over temperature',
      seed: 20260806,
      variables: [
        {
          id: 'Vin',
          name: 'Supply',
          unit: 'V',
          distribution: { kind: 'normal', mu: 5, sigma: 0.0167 },
        },
        {
          id: 'T',
          name: 'Ambient temperature',
          unit: '°C',
          distribution: { kind: 'uniform', min: -40, max: 85 },
        },
        {
          id: 'R1',
          name: 'Top resistor',
          unit: 'Ω',
          group: 'reel',
          distribution: { kind: 'uniform', min: 9900, max: 10100 },
        },
        {
          id: 'R2',
          name: 'Bottom resistor',
          unit: 'Ω',
          group: 'reel',
          distribution: { kind: 'uniform', min: 9900, max: 10100 },
        },
        {
          id: 'tcr',
          name: 'Temperature coefficient',
          unit: '1/°C',
          distribution: { kind: 'normal', mu: 100e-6, sigma: 20e-6 },
        },
      ],
      groups: [{ id: 'reel', rho: 0.8, label: 'Same reel — parts track each other' }],
      intermediates: [
        { id: 'R1e', expr: 'R1 * (1 + tcr * (T - 25))', unit: 'Ω' },
        { id: 'R2e', expr: 'R2 * (1 + tcr * (T - 25))', unit: 'Ω' },
      ],
      outputs: [
        {
          id: 'Vout',
          expr: 'Vin * R2e / (R1e + R2e)',
          unit: 'V',
          lsl: 2.45,
          usl: 2.55,
          target: 2.5,
        },
      ],
    }),
  },
  {
    id: 'stackup',
    title: 'Mechanical stackup clearance',
    blurb:
      'A four-part linear stack with a screened housing dimension. Shows worst case against statistical, and where the clearance really goes.',
    build: () => ({
      schemaVersion: 1,
      name: 'Mechanical stackup clearance',
      seed: 4242,
      variables: [
        {
          id: 'housing',
          name: 'Housing bore depth',
          unit: 'mm',
          distribution: { kind: 'truncnormal', mu: 40, sigma: 0.0333, min: 39.9, max: 40.1 },
        },
        {
          id: 'shaft',
          name: 'Shaft length',
          unit: 'mm',
          distribution: { kind: 'normal', mu: 25, sigma: 0.02 },
        },
        {
          id: 'bearing',
          name: 'Bearing width',
          unit: 'mm',
          distribution: { kind: 'normal', mu: 8, sigma: 0.0133 },
        },
        {
          id: 'shim',
          name: 'Shim thickness',
          unit: 'mm',
          distribution: { kind: 'discrete', values: [5.9, 6.0, 6.1], weights: [1, 3, 1] },
        },
      ],
      outputs: [
        {
          id: 'clearance',
          expr: 'housing - shaft - bearing - shim',
          unit: 'mm',
          lsl: 0.5,
          usl: 1.5,
          target: 1.0,
        },
      ],
    }),
  },
  {
    id: 'nonmonotonic',
    title: 'Non-monotonic response',
    blurb:
      'Power delivered to a load peaks when the source resistance matches it — in the middle of the tolerance range, not at either end. Corner-based worst case misses the peak entirely; this shows the tool refusing to.',
    build: () => ({
      schemaVersion: 1,
      name: 'Non-monotonic response',
      seed: 777,
      variables: [
        {
          // The maximum-power point sits at Rs = Rload = 100 Ω, so the range
          // must straddle it for the peak to be interior. A range that stops
          // short is monotonic, and the tool correctly says so.
          id: 'Rs',
          name: 'Source resistance',
          unit: 'Ω',
          distribution: { kind: 'uniform', min: 20, max: 300 },
        },
        {
          id: 'Vs',
          name: 'Source voltage',
          unit: 'V',
          distribution: { kind: 'normal', mu: 10, sigma: 0.1 },
        },
        {
          id: 'Rload',
          name: 'Load resistance',
          unit: 'Ω',
          distribution: { kind: 'fixed', value: 100 },
        },
      ],
      outputs: [
        {
          id: 'Pload',
          expr: 'Vs^2 * Rs / (Rs + Rload)^2',
          unit: 'W',
          usl: 0.24,
        },
      ],
    }),
  },
];

export function emptyProject(): Project {
  return {
    schemaVersion: 1,
    name: 'Untitled analysis',
    seed: Math.floor(Math.random() * 0xffffffff),
    variables: [
      { id: 'x', distribution: { kind: 'uniform', min: 0.99, max: 1.01 } },
    ],
    outputs: [{ id: 'y', expr: 'x' }],
  };
}
